/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AptosAccountInfo } from '../models/AptosAccountInfo';
import type { AptosAddressActivityListInfo } from '../models/AptosAddressActivityListInfo';
import type { AptosAddressCoinListInfo } from '../models/AptosAddressCoinListInfo';
import type { AptosAddressDetailInfo } from '../models/AptosAddressDetailInfo';
import type { AptosAddressListInfo } from '../models/AptosAddressListInfo';
import type { AptosAddressModuleInfo } from '../models/AptosAddressModuleInfo';
import type { AptosAddressNftListInfo } from '../models/AptosAddressNftListInfo';
import type { AptosAddressPortfolioCoinListInfo } from '../models/AptosAddressPortfolioCoinListInfo';
import type { AptosAddressResourceInfo } from '../models/AptosAddressResourceInfo';
import type { AptosAddressTotalCountInfo } from '../models/AptosAddressTotalCountInfo';
import type { AptosBalanceRankInfo } from '../models/AptosBalanceRankInfo';
import type { AptosCoinTransferListInfo } from '../models/AptosCoinTransferListInfo';
import type { AptosTopAddressSummaryListInfo } from '../models/AptosTopAddressSummaryListInfo';
import type { AptosTrxListInfo } from '../models/AptosTrxListInfo';
import type { AptosUawAddressCountChartListInfo } from '../models/AptosUawAddressCountChartListInfo';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AptosAddressControllerService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @returns AptosAddressDetailInfo OK
     * @throws ApiError
     */
    public getAptosAddressDetail({
        address,
    }: {
        address: string,
    }): CancelablePromise<AptosAddressDetailInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address',
            query: {
                'address': address,
            },
        });
    }

    /**
     * @returns AptosUawAddressCountChartListInfo OK
     * @throws ApiError
     */
    public getAptosUawAddressCount(): CancelablePromise<AptosUawAddressCountChartListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/uaw/count',
        });
    }

    /**
     * @returns AptosCoinTransferListInfo OK
     * @throws ApiError
     */
    public getAptosTransferListByAddress({
        address,
        page = 1,
        size = 25,
    }: {
        address: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosCoinTransferListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/transfer/list',
            query: {
                'address': address,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosTrxListInfo OK
     * @throws ApiError
     */
    public getAptosTransactionListByAddress({
        address,
        page = 1,
        size = 25,
    }: {
        address: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosTrxListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/transaction/list',
            query: {
                'address': address,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosAddressTotalCountInfo OK
     * @throws ApiError
     */
    public getAptosAddressTotalCount(): CancelablePromise<AptosAddressTotalCountInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/totalcount',
        });
    }

    /**
     * @returns AptosTopAddressSummaryListInfo OK
     * @throws ApiError
     */
    public getAptosTopAddressSummary({
        sortField = 'BALANCE',
        sortDirection = 'DESC',
        page = 1,
        size = 25,
    }: {
        sortField?: 'BALANCE' | 'COIN' | 'NFT' | 'TRANSACTION' | 'FIRST_TRANSACTION' | 'LAST_TRANSACTION',
        sortDirection?: 'ASC' | 'DESC',
        page?: number,
        size?: number,
    }): CancelablePromise<AptosTopAddressSummaryListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/top/summary',
            query: {
                'sortField': sortField,
                'sortDirection': sortDirection,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosAddressResourceInfo OK
     * @throws ApiError
     */
    public getAptosAddressResourceList({
        address,
    }: {
        address: string,
    }): CancelablePromise<Array<AptosAddressResourceInfo>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/resource/list',
            query: {
                'address': address,
            },
        });
    }

    /**
     * @returns AptosAddressNftListInfo OK
     * @throws ApiError
     */
    public getAptosAddressNftV2List({
        address,
        page = 1,
        size = 25,
    }: {
        address: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosAddressNftListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/portfolio/nft/v2/list',
            query: {
                'address': address,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosAddressNftListInfo OK
     * @throws ApiError
     */
    public getAptosAddressNftV1List({
        address,
        page = 1,
        size = 25,
    }: {
        address: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosAddressNftListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/portfolio/nft/v1/list',
            query: {
                'address': address,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosAddressPortfolioCoinListInfo OK
     * @throws ApiError
     */
    public getAptosAddressPortfolioCoinList({
        address,
        page = 1,
        size = 25,
    }: {
        address: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosAddressPortfolioCoinListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/portfolio/fa/list',
            query: {
                'address': address,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosAddressPortfolioCoinListInfo OK
     * @throws ApiError
     */
    public getAptosAddressPortfolioCoinList1({
        address,
        page = 1,
        size = 25,
    }: {
        address: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosAddressPortfolioCoinListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/portfolio/coin/list',
            query: {
                'address': address,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosAddressActivityListInfo OK
     * @throws ApiError
     */
    public getAptosNftTransferByAddress({
        address,
        page = 1,
        size = 25,
    }: {
        address: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosAddressActivityListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/nft/list',
            query: {
                'address': address,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosAddressModuleInfo OK
     * @throws ApiError
     */
    public getAptosAddressModuleList({
        address,
    }: {
        address: string,
    }): CancelablePromise<Array<AptosAddressModuleInfo>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/module/list',
            query: {
                'address': address,
            },
        });
    }

    /**
     * @returns AptosAddressListInfo OK
     * @throws ApiError
     */
    public getAptosAddressList({
        page = 1,
        size = 25,
    }: {
        page?: number,
        size?: number,
    }): CancelablePromise<AptosAddressListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/list',
            query: {
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosAccountInfo OK
     * @throws ApiError
     */
    public getAptosAccountInfo({
        address,
    }: {
        address: string,
    }): CancelablePromise<AptosAccountInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/info',
            query: {
                'address': address,
            },
        });
    }

    /**
     * @deprecated
     * Replaced ~/aptos/address/portfolio/coin/list API.
     * @returns AptosAddressCoinListInfo OK
     * @throws ApiError
     */
    public getAptosAddressCoinList({
        address,
    }: {
        address: string,
    }): CancelablePromise<AptosAddressCoinListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/coin/list',
            query: {
                'address': address,
            },
        });
    }

    /**
     * @returns AptosBalanceRankInfo OK
     * @throws ApiError
     */
    public getAptosRankByBalance({
        address,
    }: {
        address: string,
    }): CancelablePromise<AptosBalanceRankInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/address/balance/rank',
            query: {
                'address': address,
            },
        });
    }

}
